/** Web 商品目录，沿用支付页卡片/表格框架；默认与覆盖均展示整份配置。 */
import type { WebPaymentProductResponse, WebProductDevicePlatform } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { WEB_VERIFICATION_LABELS } from '~/data/adapters/web-payment-products-adapter';
import { useWebPaymentProducts } from '~/data/queries';
import { webPaymentProductsApi } from '~/hooks/use-web-payment-products';
import { useAuthStore } from '~/stores/auth-store';
import { ConfirmDialog } from '~/ui/Dialog';
import { FilterButton } from '~/ui/FilterButton';
import { Pagination } from '~/ui/Pagination';
import { SearchInput } from '~/ui/SearchInput';
import { toast } from '~/ui/Toast';
import type { PanelApp } from './PaywallProductsPanel';
import {
  WebPaymentProductEditor,
  type WebProductEditorProps,
} from './dialogs/WebPaymentProductEditor';

export function WebPaymentProductsPanel({ app }: { app: PanelApp }) {
  const [query, setQuery] = useState('');
  const [kinds, setKinds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(18);
  const [editor, setEditor] = useState<Omit<WebProductEditorProps, 'appId' | 'onClose'> | null>(
    null,
  );
  const [remove, setRemove] = useState<{
    product: WebPaymentProductResponse;
    device?: 'IOS' | 'ANDROID';
  } | null>(null);
  const role = useAuthStore((s) => s.user?.role);
  const canWrite = role === 'root' || role === 'admin' || role === 'operator';
  const canManage = role === 'root' || role === 'admin';
  const products = useWebPaymentProducts(app.id, {
    page,
    limit,
    search: query || undefined,
    product_type: kinds[0] as 'CONSUMABLE' | 'SUBSCRIPTION' | undefined,
  });
  const queryClient = useQueryClient();
  const deletion = useMutation({
    mutationFn: async (target: NonNullable<typeof remove>) => {
      return target.device
        ? webPaymentProductsApi.removeVariant(target.product.id, target.device)
        : webPaymentProductsApi.remove(target.product.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['web-payment-products', app.id] });
      setRemove(null);
      toast('Web 商品配置已移除', { tone: 'pos' });
    },
    onError: (e) => toast(e.message, { tone: 'neg' }),
  });
  const data = products.data;
  // 删除、并发下架或筛选结果缩小时修正实际查询页，分页组件的显示钳制不会触发请求。
  useEffect(() => {
    if (!products.isSuccess || !data) return;
    const lastPage = Math.max(1, Math.ceil(data.total / limit));
    if (page > lastPage) setPage(lastPage);
  }, [products.isSuccess, data, limit, page]);
  return (
    <>
      <div className="card">
        <div className="page-header">
          <SearchInput
            value={query}
            onChange={(value) => {
              setQuery(value.target.value);
              setPage(1);
            }}
            placeholder="搜索 Web 商品识别名"
          />
          <FilterButton
            label="商品类型"
            multi={false}
            options={[
              { value: 'CONSUMABLE', label: '金币包' },
              { value: 'SUBSCRIPTION', label: 'VIP 阅读订阅' },
            ]}
            value={kinds}
            onChange={(value) => {
              setKinds(value);
              setPage(1);
            }}
          />
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canWrite || app.id === 'all'}
            onClick={() => setEditor({})}
          >
            新建 Web 商品
          </button>
        </div>
        <p>
          桌面使用 DEFAULT；iOS / Android 无覆盖时使用 DEFAULT。覆盖下架时不回落，移除覆盖后才回落。
        </p>
        {products.isPending && <p>正在读取商品与 Stripe 核验状态…</p>}
        {products.isError && (
          <div role="alert">
            <p>{products.error.message}</p>
            <button type="button" className="btn btn-ghost" onClick={() => products.refetch()}>
              重试
            </button>
          </div>
        )}
        {data?.data.length === 0 && <p>暂无 Web 商品，请先创建商品与默认草稿配置。</p>}
      </div>
      {data?.data.map((product) => (
        <div className="card" key={product.id}>
          <div className="page-header">
            <strong>{product.name}</strong>
            <span className="chip">
              {product.product_type === 'SUBSCRIPTION' ? 'VIP 阅读订阅' : '金币包'}
            </span>
            <code>{product.access_level_id}</code>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={!canWrite}
              onClick={() => setEditor({ product })}
            >
              编辑文案
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={!canManage}
              onClick={() => setRemove({ product })}
            >
              删除商品
            </button>
          </div>
          <table className="tbl">
            <thead>
              <tr>
                <th>设备配置</th>
                <th>Stripe Product / Price</th>
                <th>标价 / 划线价</th>
                <th>权益 / 周期</th>
                <th>排序</th>
                <th title="实际可购还需符合设备、地区及支付方式策略">商品资格与核验状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {(['DEFAULT', 'IOS', 'ANDROID'] as WebProductDevicePlatform[]).map((device) => {
                const variant = product.variants.find((v) => v.device_platform === device);
                return (
                  <tr key={device}>
                    <td>{device}</td>
                    <td>
                      {variant?.vendor_product_id === null ? (
                        '无 Stripe 映射'
                      ) : variant ? (
                        <>
                          <code>{variant.vendor_product_id}</code>
                          <br />
                          <code>{variant.stripe_price_id}</code>
                        </>
                      ) : (
                        '未配置'
                      )}
                    </td>
                    <td>
                      {variant
                        ? `${variant.currency} ${variant.amount_minor_units} / ${variant.original_amount_minor_units ?? '—'}（最小单位）`
                        : '使用 DEFAULT'}
                    </td>
                    <td>
                      {variant
                        ? product.product_type === 'SUBSCRIPTION'
                          ? `VIP · ${variant.subscription_period === 'WEEKLY' ? '每周' : '每月'}`
                          : `${variant.coin_amount} coin + ${variant.bonus_amount} BONUS`
                        : '使用 DEFAULT'}
                    </td>
                    <td>{variant?.sort_order ?? '—'}</td>
                    <td>
                      {variant ? (
                        <>
                          {variant.is_active ? '上架' : '下架'} ·{' '}
                          {WEB_VERIFICATION_LABELS[variant.stripe_verification_status]}
                          {/* is_serveable 只表示 Stripe 支付方式下的可售（A8 §3.1.1），无映射变体不显示 */}
                          {variant.vendor_product_id !== null &&
                            ` · ${variant.is_serveable ? '商品级可售' : '商品级不可售'}`}
                        </>
                      ) : (
                        '默认回落'
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={!canManage}
                        onClick={() => setEditor({ product, device })}
                      >
                        {variant ? '编辑完整配置' : '添加整份覆盖'}
                      </button>
                      {variant && device !== 'DEFAULT' && (
                        <button
                          type="button"
                          className="btn btn-danger"
                          disabled={!canManage}
                          onClick={() => setRemove({ product, device })}
                        >
                          移除覆盖
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
      <Pagination
        total={data?.total ?? 0}
        page={page}
        pageSize={limit}
        onChange={setPage}
        onPageSizeChange={(value) => {
          setLimit(value);
          setPage(1);
        }}
      />
      {editor && (
        <WebPaymentProductEditor {...editor} appId={app.id} onClose={() => setEditor(null)} />
      )}
      {remove && (
        <ConfirmDialog
          title={remove.device ? `移除 ${remove.device} 覆盖` : '删除 Web 商品'}
          message={
            remove.device
              ? '仅停止该覆盖的新购，之后回落 DEFAULT；历史成交保留。'
              : '停止该商品的新购，历史成交与续费保持。'
          }
          tone="danger"
          onConfirm={() => {
            if (!deletion.isPending) deletion.mutate(remove);
          }}
          onClose={() => {
            if (!deletion.isPending) setRemove(null);
          }}
        />
      )}
    </>
  );
}
